import { useEffect, useRef } from 'react';
import {
  Alert,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { PlusIcon, RefreshCwIcon, Trash2Icon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { canAddRows } from '@/lib/credit-conversion-settings/tables';
import { nextDay } from '@/lib/credit-conversion-settings/validation';

import { cellKey, DATE_FIELD, type EditorErrors, type EditorPart } from './editorModel';
import type { EditorAction, EditorState } from './editorReducer';
import { columnWidthSx, fitContentSx, tableSx } from './tableLayout';
import { FROZEN_NOTICE, type ActionFailure } from './useConversionSettings';

export interface ValueEditorProps {
  editor: EditorState;
  failure: ActionFailure | null;
  busy: boolean;
  /** 保存できたかが分からない旨を表示した後。入力を変えられず、同じ内容のままの再度の保存と「再読み込み」だけができる */
  frozen: boolean;
  dispatch: (action: EditorAction) => void;
  onSave: () => void;
  onCancel: () => void;
  onReload: () => void;
}

/** 数値の入力欄に渡す props */
function numberField(
  key: string,
  label: string,
  errors: EditorErrors,
  busy: boolean,
  dispatch: (action: EditorAction) => void,
) {
  return {
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      dispatch({ type: 'change', key, value: e.target.value }),
    onBlur: () => dispatch({ type: 'blur', key }),
    error: key in errors.fields,
    helperText: errors.fields[key],
    disabled: busy,
    slotProps: {
      htmlInput: {
        inputMode: 'decimal' as const,
        'aria-label': label,
        'data-error': key in errors.fields ? 'true' : undefined,
      },
    },
  };
}

/** 編集モード。表示と同じ並びのまま入力する */
export function ValueEditor({
  editor,
  failure,
  busy,
  frozen,
  dispatch,
  onSave,
  onCancel,
  onReload,
}: ValueEditorProps) {
  const { errors } = editor;
  const container = useRef<HTMLDivElement>(null);
  const locked = busy || frozen;
  const multiple = editor.parts.length > 1;

  // 「保存」の押下時にエラーがあれば、画面で最も上にあるエラーが見える位置までスクロールする
  useEffect(() => {
    if (editor.scrollToError === 0) return;
    container.current?.querySelector('[data-error="true"]')?.scrollIntoView({ block: 'center' });
  }, [editor.scrollToError]);

  // 「行を追加」した行が見える位置までスクロールし、その行の最初の入力欄にフォーカスを置く
  useEffect(() => {
    if (!editor.focusRowKey) return;
    const row = container.current?.querySelector(`[data-row-key="${editor.focusRowKey}"]`);
    row?.scrollIntoView({ block: 'nearest' });
    row?.querySelector('input')?.focus();
    dispatch({ type: 'focusedRow' });
  }, [editor.focusRowKey, dispatch]);

  return (
    <Box ref={container}>
      <Typography variant="subtitle1" fontWeight="bold" mb={1.5}>
        {editor.action === 'create' ? '値の追加' : '値の編集'}
      </Typography>
      <TextField
        type="date"
        label="適用開始日"
        size="small"
        value={editor.date}
        // 最小値は選択の目安。ブラウザ標準のバリデーションでは止めず、画面のチェックでエラーを表示する
        slotProps={{
          inputLabel: { shrink: true },
          htmlInput: {
            min: nextDay(editor.today),
            'data-error': DATE_FIELD in errors.fields ? 'true' : undefined,
          },
        }}
        onChange={(e) => dispatch({ type: 'change', key: DATE_FIELD, value: e.target.value })}
        onBlur={() => dispatch({ type: 'blur', key: DATE_FIELD })}
        error={DATE_FIELD in errors.fields}
        helperText={errors.fields[DATE_FIELD] ?? `当日（${editor.today}）より後の日`}
        disabled={locked}
        sx={{ mb: 2 }}
      />

      <Box display="flex" flexDirection="column" gap={2}>
        {editor.parts.map((part) => (
          <RowsEditor
            key={part.table.name}
            part={part}
            label={multiple ? part.table.label : undefined}
            editor={editor}
            busy={locked}
            dispatch={dispatch}
          />
        ))}
      </Box>

      {/* 保存できたかが分からない旨を表示した後は、どの失敗の後も「再読み込み」で入力内容を破棄して取得し直せるようにする */}
      {(failure || frozen) && (
        <Alert
          severity={failure ? 'error' : 'warning'}
          sx={{ mt: 2 }}
          action={
            failure?.reload || frozen ? (
              <Button size="sm" variant="outline" onClick={onReload} disabled={busy}>
                <RefreshCwIcon />
                再読み込み
              </Button>
            ) : undefined
          }
        >
          {failure ? failure.message : FROZEN_NOTICE}
        </Alert>
      )}

      <Box display="flex" gap={1} mt={2}>
        <Button onClick={onSave} disabled={busy}>
          保存
        </Button>
        {!frozen && (
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            キャンセル
          </Button>
        )}
      </Box>
    </Box>
  );
}

/**
 * 1つのテーブルの行の一覧の入力。行にも入力欄にも属さないエラーは、その一覧の上に表示する。
 * 設定項目のテーブルが2つあるときは、読み上げで区別できるよう、aria-labelをテーブルの名前にする。
 */
function RowsEditor({
  part,
  label,
  editor,
  busy,
  dispatch,
}: {
  part: EditorPart;
  label?: string;
  editor: EditorState;
  busy: boolean;
  dispatch: (action: EditorAction) => void;
}) {
  const { table, rows } = part;
  const { errors } = editor;
  const rowsEditable = canAddRows(table);
  const tableErrors = errors.tables[table.name] ?? [];
  return (
    <Box>
      {tableErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 1.5 }} data-error="true">
          {tableErrors.map((message) => (
            <div key={message}>{message}</div>
          ))}
        </Alert>
      )}

      <TableContainer sx={fitContentSx}>
        <Table size="small" aria-label={label ? `${label}の入力` : '値の入力'} sx={tableSx}>
          <TableHead>
            <TableRow>
              {table.columns.map((c) => (
                <TableCell key={c.name} sx={columnWidthSx(c)}>
                  {c.header}
                </TableCell>
              ))}
              {rowsEditable && <TableCell width={56} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const rowError = errors.rows[row.key];
              return [
                <TableRow
                  key={row.key}
                  data-row-key={row.key}
                  sx={
                    rowError
                      ? (theme) => ({
                          bgcolor: alpha(theme.palette.error.main, 0.06),
                          outline: `1px solid ${theme.palette.error.main}`,
                        })
                      : undefined
                  }
                >
                  {table.columns.map((c) => (
                    <TableCell key={c.name} sx={{ verticalAlign: 'top' }}>
                      {c.kind === 'consumption_item' ? (
                        <>
                          <Typography variant="body2" pt={1}>
                            {editor.consumptionLabels[row.values[c.name]] ?? row.values[c.name]}
                          </Typography>
                          {errors.fields[cellKey(row.key, c.name)] && (
                            <Typography variant="caption" color="error" data-error="true">
                              {errors.fields[cellKey(row.key, c.name)]}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <TextField
                          size="small"
                          fullWidth
                          value={row.values[c.name] ?? ''}
                          {...numberField(
                            cellKey(row.key, c.name),
                            c.header,
                            errors,
                            busy,
                            dispatch,
                          )}
                        />
                      )}
                    </TableCell>
                  ))}
                  {rowsEditable && (
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <IconButton
                        aria-label="行を削除"
                        onClick={() => dispatch({ type: 'removeRow', rowKey: row.key })}
                        disabled={busy}
                        size="small"
                      >
                        <Trash2Icon size={18} />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>,
                rowError && (
                  <TableRow key={`${row.key}-error`}>
                    <TableCell
                      colSpan={table.columns.length + (rowsEditable ? 1 : 0)}
                      sx={{ border: 0, pt: 0 }}
                    >
                      <Typography variant="caption" color="error" data-error="true">
                        {rowError}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ),
              ];
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {rowsEditable && (
        <Button
          size="sm"
          variant="outline"
          className="mt-2"
          onClick={() => dispatch({ type: 'addRow', table: table.name })}
          disabled={busy}
        >
          <PlusIcon />
          行を追加
        </Button>
      )}
    </Box>
  );
}
